import { create } from 'zustand';

/**
 * App-level notice for a page the current admin may not open (SHCT-171).
 *
 * PRD 21 §7.2 lists 권한 없음 → "접근 권한이 없습니다." as a **모달**, and §7.3 repeats it for 403
 * ("접근 권한이 없습니다." 팝업). The same table uses 토스트 for network/save failures, so the modal is a
 * deliberate distinction, not loose wording — and our Error Standard §7.1 independently classes a
 * permission failure as a Business Logic Error → Modal 필수, toast 금지. This replaces the toast that
 * {@link useRequirePermission} used to raise.
 *
 * It lives outside the denied page because that page renders nothing: the gate returns `null`, so
 * there is no component left to host a dialog. {@link PermissionNoticeHost} is mounted once in
 * `AdminLayout`, which outlives the redirect to /home that dismissing the notice triggers.
 */
interface PermissionNoticeState {
  open: boolean;
  show: () => void;
  clear: () => void;
}

export const usePermissionNoticeStore = create<PermissionNoticeState>((set) => ({
  open: false,
  // Never re-open over a notice the user has not acknowledged. A denied page can re-render (a query
  // settling, a parent state change) while the dialog is up, and each render would otherwise reset it.
  show: () => set((s) => (s.open ? s : { open: true })),
  clear: () => set({ open: false }),
}));

/** Imperative entry point, for callers that are not components. */
export const permissionNotice = {
  show: () => usePermissionNoticeStore.getState().show(),
  // SHCT-629 (re-test) — a notice raised on a stale permission map has to be retractable. When the
  // fresh read says the admin DOES have access, leaving the dialog up would make them acknowledge
  // a refusal that no longer applies — and acknowledging redirects to /home.
  clear: () => usePermissionNoticeStore.getState().clear(),
};
